import React, { useCallback, useMemo, useState } from "react"
import {
  Box,
  BoxProps,
  Input,
  InputGroup,
  InputProps,
  InputLeftElement,
  InputRightElement,
  FormLabel,
  Text,
  HStack,
  StackDivider,
} from "@chakra-ui/react"
// @ts-ignore
import { validate } from "react-email-validator"
import Icon from "../Icon/Icon"

export interface FormInputProps extends InputProps {
  autoFocus?: boolean
  background?: string
  country?: string | null
  containerProps?: BoxProps
  errors?: Array<string>
  valid?: boolean
  iconLeft?: React.ReactElement
  iconRight?: React.ReactElement
  iconEvents?: boolean
  name: string
  label?: string
  placeholder?: string
  reference?: any
  type?: string
  value?: string
  id?: string
}

export const FormInput = React.forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      autoFocus = false,
      background = "white",
      containerProps,
      errors = [],
      valid,
      iconLeft,
      iconRight,
      iconEvents = false,
      name,
      onBlur,
      onFocus,
      label,
      placeholder,
      type = "text",
      value,
      variant,
      id: rawId,
      ...props
    },
    ref
  ) => {
    const [focus, setFocus] = useState(false)
    const id = useMemo(() => `${name || placeholder || ""}_${rawId || "input"}`, [name, placeholder, rawId])
    const isInvalid = useMemo(() => (type === "email" && value && !validate(value) ? true : false), [type, value])
    const showLabel = label && !value?.trim()

    const showTickIcon = Boolean(!errors.length && valid)
    const showErrorIcon = Boolean(errors.length)

    const rightIcons: React.ReactElement[] = !props.isDisabled
      ? [
          ...(showTickIcon ? [<Icon key="tick" name="tick" color="system.success.light" size="22px" />] : []),
          ...(showErrorIcon ? [<Icon key="error" name="error" color="brand.sale" size="22px" />] : []),
          ...(iconRight ? [iconRight] : [null]),
        ]
      : []

    const handleBlur = useCallback(
      (event: any) => {
        setFocus((prevState: boolean) => !prevState)

        if (onBlur) onBlur(event)
      },
      [onBlur, setFocus]
    )

    const handleFocus = useCallback(
      (event: any) => {
        setFocus((prevState: boolean) => !prevState)

        if (onFocus) onFocus(event)
      },
      [onFocus, setFocus]
    )

    return (
      <Box {...containerProps}>
        <InputGroup alignItems="center" pos="relative" transition="color 0.2s ease">
          {iconLeft && <InputLeftElement pointerEvents={iconEvents ? "all" : "none"}>{iconLeft}</InputLeftElement>}
          <Input
            ref={ref}
            autoFocus={autoFocus}
            css={{
              "&::-webkit-search-results-decoration,&::-webkit-search-results-button,&::-webkit-search-cancel-button,&::-webkit-search-decoration":
                {
                  display: "none",
                },
              "&::-ms-clear,&::-ms-reveal": {
                display: "none",
              },
            }}
            id={id}
            isInvalid={errors?.length > 0 || isInvalid}
            name={name}
            onBlur={handleBlur}
            onFocus={handleFocus}
            px={iconLeft ? 4.5 : 2}
            pb="3px"
            type={type}
            value={value}
            variant={variant}
            data-peer
            placeholder={label ? "" : placeholder}
            {...props}
          />
          {showLabel && (
            <FormLabel
              bg={focus ? background : "transparent"}
              htmlFor={id}
              isTruncated
              maxW="calc(100% - 24px)"
              ml={!(focus || value) && iconLeft ? 4 : 2}
              p={0.5}
              pointerEvents="none"
              pos="absolute"
              size={focus || value ? "sm" : "md"}
              transition="all 0.3s ease"
              transform={focus ? `translateY(-100%)` : "translateY(20%)"}
              zIndex={1}
            >
              {label}
            </FormLabel>
          )}
          <InputRightElement pr={1.5} w="unset">
            <HStack divider={<StackDivider />} h="70%" spacing={1.5}>
              {rightIcons}
            </HStack>
          </InputRightElement>
        </InputGroup>

        {errors?.length > 0 && (
          <>
            {errors?.map(message => (
              <Text key={message?.toString()} color="brand.sale" mt={0.5} size="xs" textAlign="left">
                {message?.toString()}
              </Text>
            ))}
          </>
        )}
      </Box>
    )
  }
)
